/**
 * Most of the logic here has been yeeted from dash's codebase.
 *
 * I've put in some effort to strip away the unnecessary logic, yet some of it has to stay
 */

import { useEffect, useRef, useState } from 'react';

import { Tab } from '@/decodo/components/atoms/Tabs/Tab';
import { debounce } from '@smartproxy-web/shared/utils';
import { useCallback } from 'react';

// implementation yeeted from Dash
export const useOnResize = (
  handler: (e: Event) => void,
  runOnMount = () => {}
) => {
  const debouncedHandler = useCallback(debounce(handler, 50), [handler]);
  useEffect(() => {
    runOnMount && runOnMount();
  }, []);
  useEffect(() => {
    window.addEventListener('resize', debouncedHandler);
    return () => {
      window.removeEventListener('resize', debouncedHandler);
    };
  }, [debouncedHandler]);
};

// amount to scroll by when pressing one of the arrows
const DEFAULT_SCROLL_WIDTH_PX = 200;

// handle occasionally inconsistent ref scroll calculations
const CONSISTENT_SCROLL_TOLERANCE_PX = 1;

// this matches gap-8 in the component's JSX. Match them if they change.
const TAB_GAP_PX = 32;

interface OverflowTabsResizeProps<T extends string> {
  tabs: Tab<T>[];
  tabsRef: React.RefObject<HTMLDivElement | null>;
  tabsContainerRef: React.RefObject<HTMLUListElement | null>;
}

const calculateTabWidths = (tabElements: HTMLElement[], tabGap: number) => {
  const widths = tabElements.map((el) => el.offsetWidth + tabGap);

  const cumulativeSum = (
    (sum) => (value: number) =>
      (sum += value)
  )(0);

  return widths.map(cumulativeSum);
};
export const useShowScrollButtons = <T extends string>({
  tabs,
  tabsRef,
  tabsContainerRef,
}: OverflowTabsResizeProps<T>) => {
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const widths = useRef<Array<number>>([]);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const updateIsScrollable = () => {
    if (!tabsContainerRef?.current || !tabsRef?.current) return;

    const containerWidth = tabsContainerRef.current.offsetWidth;
    const tabsWidth = tabsRef.current.scrollWidth;

    setIsScrollable(tabsWidth > containerWidth);
  };

  const calculateWidths = () => {
    if (!tabsRef?.current) return;

    const tabElements = Array.from(tabsRef.current.children) as HTMLElement[];
    widths.current = calculateTabWidths(tabElements, TAB_GAP_PX);
  };

  const updateScrollButtons = () => {
    if (!tabsContainerRef?.current) return;

    const scrollLeftValue = tabsContainerRef.current.scrollLeft;
    const scrollWidth = tabsContainerRef.current.scrollWidth;
    const clientWidth = tabsContainerRef.current.clientWidth;

    setShowLeftButton(scrollLeftValue > 0 + CONSISTENT_SCROLL_TOLERANCE_PX);
    setShowRightButton(
      scrollLeftValue <
        scrollWidth - clientWidth - CONSISTENT_SCROLL_TOLERANCE_PX
    );
  };

  const handleScrollLeft = () => {
    if (!tabsContainerRef?.current) return;

    tabsContainerRef.current.scrollTo({
      left: tabsContainerRef.current.scrollLeft - DEFAULT_SCROLL_WIDTH_PX,
      behavior: 'smooth',
    });
    updateScrollButtons();
  };

  const handleScrollRight = () => {
    if (!tabsContainerRef?.current) return;

    tabsContainerRef.current.scrollTo({
      left: tabsContainerRef.current.scrollLeft + DEFAULT_SCROLL_WIDTH_PX,
      behavior: 'smooth',
    });
    updateScrollButtons();
  };

  const handleMouseDown = (e: MouseEvent) => {
    if (!tabsContainerRef?.current) return;

    isDragging.current = true;
    startX.current = e.pageX - tabsContainerRef.current.offsetLeft;
    scrollLeft.current = tabsContainerRef.current.scrollLeft;
    tabsContainerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseLeave = () => {
    if (!tabsContainerRef?.current) return;

    isDragging.current = false;
    tabsContainerRef.current.style.cursor = 'grab';
  };

  const handleMouseUp = () => {
    if (!tabsContainerRef?.current) return;

    isDragging.current = false;
    tabsContainerRef.current.style.cursor = 'grab';
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!tabsContainerRef?.current) return;
    if (!isDragging.current) return;

    const x = e.pageX - tabsContainerRef.current.offsetLeft;
    const walk = x - startX.current;
    tabsContainerRef.current.scrollLeft = scrollLeft.current - walk;
  };

  useEffect(() => {
    calculateWidths();
    updateIsScrollable();
    updateScrollButtons();
  }, [tabs]);

  useOnResize(() => {
    calculateWidths();
    updateIsScrollable();
    updateScrollButtons();
  });

  useEffect(() => {
    if (!tabsContainerRef?.current) return;

    const handleScroll = () => {
      updateScrollButtons();
    };

    tabsContainerRef.current.addEventListener('scroll', handleScroll);
    tabsContainerRef.current.addEventListener('mousedown', handleMouseDown);
    tabsContainerRef.current.addEventListener('mouseleave', handleMouseLeave);
    tabsContainerRef.current.addEventListener('mouseup', handleMouseUp);
    tabsContainerRef.current.addEventListener('mousemove', handleMouseMove);

    return () => {
      tabsContainerRef.current?.removeEventListener('scroll', handleScroll);
      tabsContainerRef.current?.removeEventListener(
        'mousedown',
        handleMouseDown
      );
      tabsContainerRef.current?.removeEventListener(
        'mouseleave',
        handleMouseLeave
      );
      tabsContainerRef.current?.removeEventListener('mouseup', handleMouseUp);
      tabsContainerRef.current?.removeEventListener(
        'mousemove',
        handleMouseMove
      );
    };
  }, []);

  return {
    showLeftButton,
    showRightButton,
    handleScrollLeft,
    handleScrollRight,
    isScrollable,
  };
};
